import { sb } from '../supabase';
import { clarityStatus } from './growthClarity';
import { adStatus } from './growthAds';

// Growth OS dashboard data. Every number is counted from real rows — never
// estimated, never filled in. Events come from growth_trader_events ONLY (the
// clean events — no e2e row, team browsers or admin accounts, owner rule
// 2026-11-01 — minus every device ever signed in as a supplier, 2026-11-03); paid orders from market_orders minus orders placed by team
// accounts (the owner's own test purchases).
//
// Counted in the browser for now: fine at today's volumes. When a range
// returns the row cap, the dashboard says so instead of showing a short count.

const DAY = 864e5;
export const ROW_CAP = 20000;
const STEPS = ['session_started', 'product_viewed', 'cart_item_added', 'checkout_started', 'payment_started'];
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || '';
export const CLARITY_URL = CLARITY_ID ? `https://clarity.microsoft.com/projects/view/${CLARITY_ID}/dashboard` : null;

// Days are Saudi days, whatever the viewer's clock says.
const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh' });
export const dayKey = (iso) => dayFmt.format(new Date(iso));
const hourFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Riyadh', hour: '2-digit', hourCycle: 'h23' });
export const hourKey = (iso) => hourFmt.format(new Date(iso)).padStart(2, '0');

export function sourceOf(props = {}) {
  if (props.utm_source) return String(props.utm_source).toLowerCase();
  if (props.gclid) return 'google ads';
  if (props.fbclid) return 'meta';
  if (props.ttclid) return 'tiktok';
  if (props.referrer_domain) return String(props.referrer_domain).replace(/^www\./, '').toLowerCase();
  return 'direct';
}

function counts(events, orders) {
  const n = (name) => events.filter((e) => e.event_name === name).length;
  return {
    sessions: n('session_started'),
    productViews: n('product_viewed'),
    addToCart: n('cart_item_added'),
    paidOrders: orders.length,
    revenue: Math.round(orders.reduce((s, o) => s + (Number(o.total_sar) || 0), 0) * 100) / 100,
  };
}

// Pure: the whole dashboard from rows, so it can be tested without a database.
// `win` comes from rangeWindow(): [start, end) and the equal period before it.
// A one-day range is bucketed by Riyadh hour, longer ones by Riyadh day.
export function summarize(events, orders, win) {
  const inCur = (ms) => ms >= win.start && ms < win.end;
  const inPrev = (ms) => ms >= win.prevStart && ms < win.start;
  const cur = events.filter((e) => inCur(Date.parse(e.occurred_at)));
  const prev = events.filter((e) => inPrev(Date.parse(e.occurred_at)));
  const curO = orders.filter((o) => inCur(Date.parse(o.paid_at)));
  const prevO = orders.filter((o) => inPrev(Date.parse(o.paid_at)));

  const buckets = [];
  if (win.hourly) {
    for (let h = 0; h < 24; h++) buckets.push({ key: String(h).padStart(2, '0'), label: `${String(h).padStart(2, '0')}:00`, sessions: 0, adds: 0, paid: 0 });
  } else {
    for (let t = win.start; t < win.end; t += DAY) { const d = dayKey(new Date(t + 3600e3).toISOString()); buckets.push({ key: d, label: d, sessions: 0, adds: 0, paid: 0 }); }
  }
  const at = Object.fromEntries(buckets.map((b) => [b.key, b]));
  const bucketOf = (iso) => (win.hourly ? hourKey(iso) : dayKey(iso));
  cur.forEach((e) => {
    const b = at[bucketOf(e.occurred_at)];
    if (!b) return;
    if (e.event_name === 'session_started') b.sessions += 1;
    if (e.event_name === 'cart_item_added') b.adds += 1;
  });
  curO.forEach((o) => { const b = at[bucketOf(o.paid_at)]; if (b) b.paid += 1; });

  // funnel: sessions that reached each step (a session counts once per step)
  const sets = STEPS.map((step) => new Set(cur.filter((e) => e.event_name === step || (step === 'session_started' && e.session_id)).map((e) => e.session_id).filter(Boolean)));
  const reached = sets.map((x) => x.size);
  // Who stopped at each step (reached it, not the next) — the sessions worth watching in Clarity.
  const lastAt = {};
  cur.forEach((e) => { if (e.session_id) lastAt[e.session_id] = Math.max(lastAt[e.session_id] || 0, Date.parse(e.occurred_at)); });
  const dropsAt = (i) => (i + 1 < sets.length ? [...sets[i]].filter((sid) => !sets[i + 1].has(sid)) : [])
    .map((session_id) => ({ session_id, at: lastAt[session_id] })).sort((x, y) => y.at - x.at).slice(0, 8);
  const funnel = [
    { key: 'sessions', drops: dropsAt(0), label: 'الجلسات', n: reached[0] },
    { key: 'viewed', drops: dropsAt(1), label: 'شاهدوا منتجاً', n: reached[1] },
    { key: 'added', drops: dropsAt(2), label: 'أضافوا للسلة', n: reached[2] },
    { key: 'checkout', drops: dropsAt(3), label: 'بدأوا الطلب', n: reached[3] },
    { key: 'payment', drops: dropsAt(4), label: 'ذهبوا للدفع', n: reached[4] },
  ];
  let drop = null;
  for (let i = 1; i < funnel.length; i++) {
    const a = funnel[i - 1].n; const b = funnel[i].n;
    if (a > 0) {
      const pct = Math.round(((a - b) / a) * 1000) / 10;
      if (!drop || pct > drop.pct) drop = { from: funnel[i - 1].label, to: funnel[i].label, pct, base: a };
    }
  }

  // where sessions came from, and how many of them added to the cart
  const bySession = {};
  cur.forEach((e) => {
    if (!e.session_id) return;
    const s = bySession[e.session_id] || (bySession[e.session_id] = { source: null, added: false });
    if (e.event_name === 'session_started') s.source = sourceOf(e.props || {});
    if (e.event_name === 'cart_item_added') s.added = true;
  });
  const src = {};
  Object.values(bySession).forEach((s) => {
    if (!s.source) return;
    const r = src[s.source] || (src[s.source] = { source: s.source, sessions: 0, added: 0 });
    r.sessions += 1;
    if (s.added) r.added += 1;
  });
  const sources = Object.values(src).sort((a, b) => b.sessions - a.sessions).slice(0, 8);

  return { kpis: counts(cur, curO), prev: counts(prev, prevO), hasPrev: prev.length > 0 || prevO.length > 0, buckets, hourly: !!win.hourly, funnel, drop, sources };
}

async function paidOrdersSince(sinceIso, untilIso) {
  const { data, error } = await sb.from('market_orders').select('id, buyer_id, total_sar, paid_at')
    .not('paid_at', 'is', null).gte('paid_at', sinceIso).lt('paid_at', untilIso).limit(5000);
  if (error) throw error;
  const rows = data || [];
  const buyers = [...new Set(rows.map((r) => r.buyer_id).filter(Boolean))];
  if (!buyers.length) return rows;
  const { data: team, error: teamErr } = await sb.from('profiles').select('id').in('id', buyers).in('role', ['admin', 'super_admin']);
  if (teamErr) throw teamErr;
  const isTeam = new Set((team || []).map((p) => p.id));
  return rows.filter((r) => !isTeam.has(r.buyer_id));
}

// The selected range plus the one before it, for the change figures.
export async function fetchDashboard(win) {
  const since = new Date(win.prevStart).toISOString();
  const until = new Date(win.end).toISOString();
  const [ev, orders] = await Promise.all([
    sb.from('growth_trader_events').select('event_name, occurred_at, session_id, props')
      .in('event_name', STEPS).gte('occurred_at', since).lt('occurred_at', until)
      .order('occurred_at', { ascending: true }).limit(ROW_CAP),
    paidOrdersSince(since, until),
  ]);
  if (ev.error) throw ev.error;
  const events = ev.data || [];
  return { ...summarize(events, orders, win), capped: events.length >= ROW_CAP };
}

// The sidebar's «حالة التكاملات»: each line from a real check, or plainly "not connected".
export async function fetchIntegrationStatus() {
  let db = { state: 'ok', label: 'متصل' };
  let web = { state: 'neutral', label: 'بانتظار البيانات' };
  try {
    const { count, error } = await sb.from('growth_trader_events').select('event_id', { count: 'exact', head: true })
      .gte('received_at', new Date(Date.now() - DAY).toISOString());
    if (error) throw error;
    if (count > 0) web = { state: 'ok', label: 'يستقبل البيانات' };
  } catch {
    db = { state: 'bad', label: 'تعذّر الاستعلام' };
    web = { state: 'bad', label: 'تعذّر الاستعلام' };
  }
  let clarity = clarityStatus(null);
  try {
    const { data, error } = await sb.from('growth_clarity_pulls').select('status, started_at, finished_at').order('started_at', { ascending: false }).limit(1);
    if (!error) clarity = clarityStatus(data);
  } catch { /* table not there yet: the script-only status stands */ }
  const notLinked = { state: 'neutral', label: 'غير متصل' };
  let adPulls = [];
  try {
    const { data, error } = await sb.from('growth_ad_pulls').select('platform, status, started_at, finished_at').order('started_at', { ascending: false }).limit(20);
    if (!error) adPulls = data || [];
  } catch { /* table not there yet */ }
  return [
    { key: 'supabase', name: 'Supabase', ...db },
    { key: 'web', name: 'Maabar Web', ...web },
    // Ad platforms next, so their live status is above the fold of a laptop screen.
    { key: 'google', name: 'Google Ads', ...(adStatus(adPulls, 'google') || notLinked) },
    { key: 'snapchat', name: 'Snapchat Ads', ...(adStatus(adPulls, 'snapchat') || notLinked) },
    { key: 'meta', name: 'Meta Ads', ...(adStatus(adPulls, 'meta') || notLinked) },
    { key: 'clarity', name: 'Microsoft Clarity', ...clarity },
    { key: 'adjust', name: 'Adjust', state: 'neutral', label: 'في التطبيق فقط' },
    { key: 'tiktok', name: 'TikTok', state: 'neutral', label: 'في التطبيق فقط' },
  ];
}
